package fr.manyverse.app.presentation.screens

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.GmInventoryItem
import fr.manyverse.app.domain.model.ItemCategory
import fr.manyverse.app.domain.model.UniverseConfig
import fr.manyverse.app.domain.util.normalizeForSearch
import fr.manyverse.app.presentation.components.CheckboxRow
import fr.manyverse.app.presentation.components.clearFocusOnTap
import fr.manyverse.app.presentation.components.ErrorState
import fr.manyverse.app.presentation.components.SkeletonList
import fr.manyverse.app.presentation.components.ZoomableImageDialog
import fr.manyverse.app.presentation.components.inventory.InventoryCategoryFilterDropdown
import fr.manyverse.app.presentation.components.inventory.InventoryItemFormDialog
import fr.manyverse.app.presentation.components.inventory.InventoryItemImage
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.theme.UniverseTheme
import fr.manyverse.app.presentation.viewmodel.GmInventoryViewModel
import fr.manyverse.app.presentation.viewmodel.ThemeViewModel
import kotlinx.coroutines.launch
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.back
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.gm_inventory_add_item
import manyverse.shared.generated.resources.gm_inventory_delete_item
import manyverse.shared.generated.resources.gm_inventory_delete_item_confirm
import manyverse.shared.generated.resources.gm_inventory_edit_item
import manyverse.shared.generated.resources.gm_inventory_empty
import manyverse.shared.generated.resources.gm_inventory_error_loading
import manyverse.shared.generated.resources.gm_inventory_send_all
import manyverse.shared.generated.resources.gm_inventory_send_button
import manyverse.shared.generated.resources.gm_inventory_send_no_players
import manyverse.shared.generated.resources.gm_inventory_send_title
import manyverse.shared.generated.resources.gm_inventory_title
import manyverse.shared.generated.resources.inventory_item_quantity
import manyverse.shared.generated.resources.inventory_search_hint
import manyverse.shared.generated.resources.retry
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GmInventoryScreen(
    tableId: String,
    gmUserId: String,
    themeId: String,
    themeSeedColor: Long?,
    onNavigateBack: () -> Unit
) {
    val themeViewModel: ThemeViewModel = koinViewModel()
    val themePreference by themeViewModel.isDarkTheme.collectAsStateWithLifecycle()
    val isDarkTheme = themePreference ?: isSystemInDarkTheme()
    val universeConfig = remember(themeId, themeSeedColor) {
        UniverseConfig(themeId = themeId, themeSeedColor = themeSeedColor)
    }

    UniverseTheme(config = universeConfig, isDark = isDarkTheme) {
        val viewModel: GmInventoryViewModel = koinViewModel(
            key = "gminventory_$tableId"
        ) { parametersOf(tableId, gmUserId) }

        val itemsState by viewModel.itemsState.collectAsStateWithLifecycle()
        val createItemState by viewModel.createItemState.collectAsStateWithLifecycle()
        val updateItemState by viewModel.updateItemState.collectAsStateWithLifecycle()
        val deleteItemState by viewModel.deleteItemState.collectAsStateWithLifecycle()
        val sendItemState by viewModel.sendItemState.collectAsStateWithLifecycle()
        val playerCharacters by viewModel.playerCharacters.collectAsStateWithLifecycle()

        var selectedCategories by remember { mutableStateOf(ItemCategory.entries.toSet()) }
        var searchQuery by remember { mutableStateOf("") }
        var showAddDialog by remember { mutableStateOf(false) }
        var editTarget by remember { mutableStateOf<GmInventoryItem?>(null) }
        var deleteTarget by remember { mutableStateOf<GmInventoryItem?>(null) }
        var sendTarget by remember { mutableStateOf<GmInventoryItem?>(null) }

        LaunchedEffect(deleteItemState) {
            if (deleteItemState is AsyncUiState.Success) {
                viewModel.resetDeleteItemState()
                deleteTarget = null
            }
        }

        LaunchedEffect(createItemState) {
            if (createItemState is AsyncUiState.Success) {
                viewModel.resetCreateItemState()
                showAddDialog = false
            }
        }

        LaunchedEffect(updateItemState) {
            if (updateItemState is AsyncUiState.Success) {
                viewModel.resetUpdateItemState()
                editTarget = null
            }
        }

        LaunchedEffect(sendItemState) {
            if (sendItemState is AsyncUiState.Success) {
                viewModel.resetSendItemState()
                sendTarget = null
            }
        }

        Scaffold(
            topBar = {
                TopAppBar(
                    title = { Text(stringResource(Res.string.gm_inventory_title)) },
                    navigationIcon = {
                        IconButton(onClick = onNavigateBack) {
                            Icon(
                                imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                                contentDescription = stringResource(Res.string.back)
                            )
                        }
                    }
                )
            },
            floatingActionButton = {
                if (itemsState is AsyncUiState.Success) {
                    FloatingActionButton(
                        onClick = { showAddDialog = true },
                        containerColor = MaterialTheme.colorScheme.primaryContainer
                    ) {
                        Icon(
                            imageVector = Icons.Default.Add,
                            contentDescription = stringResource(Res.string.gm_inventory_add_item),
                            tint = MaterialTheme.colorScheme.onPrimaryContainer
                        )
                    }
                }
            }
        ) { padding ->
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding)
            ) {
                // Search bar
                OutlinedTextField(
                    value = searchQuery,
                    onValueChange = { searchQuery = it },
                    placeholder = { Text(stringResource(Res.string.inventory_search_hint)) },
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 8.dp),
                    singleLine = true
                )

                // Category filter dropdown
                InventoryCategoryFilterDropdown(
                    selectedCategories = selectedCategories,
                    onCategoriesChanged = { selectedCategories = it },
                    modifier = Modifier.padding(horizontal = 16.dp)
                )

                when (val state = itemsState) {
                    is AsyncUiState.Loading, is AsyncUiState.Idle -> {
                        SkeletonList(modifier = Modifier.fillMaxSize())
                    }

                    is AsyncUiState.Error -> {
                        ErrorState(
                            message = state.message,
                            onRetry = { viewModel.loadItems() },
                            title = stringResource(Res.string.gm_inventory_error_loading)
                        )
                    }

                    is AsyncUiState.Success -> {
                        val filtered = state.data
                            .filter { item ->
                                item.category in selectedCategories &&
                                        (searchQuery.isBlank() || item.name.normalizeForSearch()
                                            .contains(searchQuery.normalizeForSearch()))
                            }

                        if (filtered.isEmpty()) {
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(24.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = stringResource(Res.string.gm_inventory_empty),
                                    style = MaterialTheme.typography.bodyLarge,
                                    textAlign = TextAlign.Center,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        } else {
                            val scope = rememberCoroutineScope()
                            LazyVerticalGrid(
                                columns = GridCells.Adaptive(140.dp),
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(horizontal = 12.dp),
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp),
                                contentPadding = androidx.compose.foundation.layout.PaddingValues(
                                    top = 8.dp,
                                    bottom = 96.dp
                                )
                            ) {
                                items(filtered, key = { it.id }) { item ->
                                    var signedUrl by remember { mutableStateOf<String?>(null) }
                                    LaunchedEffect(item.imageUrl) {
                                        if (item.imageUrl != null) {
                                            scope.launch {
                                                viewModel.getSignedImageUrl(item.imageUrl)
                                                    .onSuccess { signedUrl = it }
                                            }
                                        } else {
                                            signedUrl = null
                                        }
                                    }
                                    GmInventoryItemCard(
                                        item = item,
                                        signedImageUrl = signedUrl,
                                        onEditClick = { editTarget = item },
                                        onDeleteClick = { deleteTarget = item },
                                        onSendClick = { sendTarget = item }
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }

        if (showAddDialog) {
            InventoryItemFormDialog(
                title = stringResource(Res.string.gm_inventory_add_item),
                itemKey = null,
                isLoading = createItemState is AsyncUiState.Loading,
                errorMessage = (createItemState as? AsyncUiState.Error)?.message,
                onConfirm = { name, category, quantity, imageData ->
                    viewModel.createItem(name, category, quantity, imageData)
                },
                onDismiss = {
                    viewModel.resetCreateItemState()
                    showAddDialog = false
                }
            )
        }

        editTarget?.let { target ->
            var editSignedUrl by remember(target.id) { mutableStateOf<String?>(null) }
            LaunchedEffect(target.imageUrl) {
                editSignedUrl = target.imageUrl?.let { viewModel.getSignedImageUrl(it).getOrNull() }
            }
            InventoryItemFormDialog(
                title = stringResource(Res.string.gm_inventory_edit_item),
                itemKey = target.id,
                initialName = target.name,
                initialCategory = target.category,
                initialQuantity = target.quantity,
                isLoading = updateItemState is AsyncUiState.Loading,
                errorMessage = (updateItemState as? AsyncUiState.Error)?.message,
                onConfirm = { name, category, quantity, imageData ->
                    viewModel.updateItem(target, name, category, quantity, imageData)
                },
                onDismiss = {
                    viewModel.resetUpdateItemState()
                    editTarget = null
                },
                currentImageUrl = editSignedUrl
            )
        }

        deleteTarget?.let { target ->
            AlertDialog(
                onDismissRequest = {
                    if (deleteItemState !is AsyncUiState.Loading) deleteTarget = null
                },
                title = { Text(stringResource(Res.string.gm_inventory_delete_item)) },
                text = {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text(stringResource(Res.string.gm_inventory_delete_item_confirm))
                        val errorMsg = (deleteItemState as? AsyncUiState.Error)?.message
                        if (errorMsg != null) {
                            Text(
                                text = errorMsg,
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.bodySmall
                            )
                        }
                    }
                },
                confirmButton = {
                    Button(
                        onClick = { viewModel.deleteItem(target) },
                        enabled = deleteItemState !is AsyncUiState.Loading
                    ) {
                        if (deleteItemState is AsyncUiState.Loading) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(16.dp),
                                strokeWidth = 2.dp
                            )
                        } else {
                            Text(stringResource(Res.string.gm_inventory_delete_item))
                        }
                    }
                },
                dismissButton = {
                    TextButton(
                        onClick = {
                            viewModel.resetDeleteItemState()
                            deleteTarget = null
                        },
                        enabled = deleteItemState !is AsyncUiState.Loading
                    ) {
                        Text(stringResource(Res.string.cancel))
                    }
                }
            )
        }

        sendTarget?.let { target ->
            GmInventorySendDialog(
                item = target,
                playerCharacters = playerCharacters,
                isLoading = sendItemState is AsyncUiState.Loading,
                errorMessage = (sendItemState as? AsyncUiState.Error)?.message,
                onConfirm = { characterIds, quantity ->
                    viewModel.sendToPlayers(target, characterIds, quantity)
                },
                onDismiss = {
                    viewModel.resetSendItemState()
                    sendTarget = null
                }
            )
        }
    }
}

@Composable
private fun GmInventoryItemCard(
    item: GmInventoryItem,
    signedImageUrl: String?,
    onEditClick: () -> Unit,
    onDeleteClick: () -> Unit,
    onSendClick: () -> Unit
) {
    var showFullscreenImage by remember { mutableStateOf(false) }

    ElevatedCard(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            InventoryItemImage(
                signedImageUrl = signedImageUrl,
                itemName = item.name,
                category = item.category,
                onClick = { if (signedImageUrl != null) showFullscreenImage = true },
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(1f)
            )

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 8.dp, vertical = 4.dp),
                verticalArrangement = Arrangement.spacedBy(2.dp)
            ) {
                Text(
                    text = item.name,
                    style = MaterialTheme.typography.titleSmall,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth()
                )
                if (item.category == ItemCategory.CONSUMABLE) {
                    Text(
                        text = "×${item.quantity}",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceEvenly
            ) {
                IconButton(onClick = onEditClick, modifier = Modifier.size(36.dp)) {
                    Icon(
                        imageVector = Icons.Default.Edit,
                        contentDescription = stringResource(Res.string.gm_inventory_edit_item),
                        modifier = Modifier.size(18.dp)
                    )
                }
                IconButton(onClick = onSendClick, modifier = Modifier.size(36.dp)) {
                    Icon(
                        imageVector = Icons.AutoMirrored.Filled.Send,
                        contentDescription = stringResource(Res.string.gm_inventory_send_title),
                        tint = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.size(18.dp)
                    )
                }
                IconButton(onClick = onDeleteClick, modifier = Modifier.size(36.dp)) {
                    Icon(
                        imageVector = Icons.Default.Delete,
                        contentDescription = stringResource(Res.string.gm_inventory_delete_item),
                        tint = MaterialTheme.colorScheme.error,
                        modifier = Modifier.size(18.dp)
                    )
                }
            }
        }
    }

    if (showFullscreenImage && signedImageUrl != null) {
        ZoomableImageDialog(
            imageUrl = signedImageUrl,
            contentDescription = item.name,
            onDismiss = { showFullscreenImage = false }
        )
    }
}

@Composable
private fun GmInventorySendDialog(
    item: GmInventoryItem,
    playerCharacters: List<Character>,
    isLoading: Boolean,
    errorMessage: String?,
    onConfirm: (characterIds: List<String>, quantity: Int) -> Unit,
    onDismiss: () -> Unit
) {
    var selectedCharacterIds by remember { mutableStateOf(emptySet<String>()) }
    var quantity by remember { mutableStateOf("1") }

    AlertDialog(
        modifier = Modifier.clearFocusOnTap(),
        onDismissRequest = { if (!isLoading) onDismiss() },
        title = { Text(stringResource(Res.string.gm_inventory_send_title)) },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                if (playerCharacters.isEmpty()) {
                    Text(
                        text = stringResource(Res.string.gm_inventory_send_no_players),
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        style = MaterialTheme.typography.bodyMedium
                    )
                } else {
                    // Select all button
                    val allSelected = selectedCharacterIds.size == playerCharacters.size
                    TextButton(
                        onClick = {
                            selectedCharacterIds = if (allSelected) emptySet()
                            else playerCharacters.map { it.id }.toSet()
                        }
                    ) {
                        Text(stringResource(Res.string.gm_inventory_send_all))
                    }

                    playerCharacters.forEach { character ->
                        CheckboxRow(
                            label = character.name,
                            checked = character.id in selectedCharacterIds,
                            onCheckedChange = { checked ->
                                selectedCharacterIds = if (checked) {
                                    selectedCharacterIds + character.id
                                } else {
                                    selectedCharacterIds - character.id
                                }
                            },
                            enabled = !isLoading
                        )
                    }

                    if (item.category == ItemCategory.CONSUMABLE) {
                        OutlinedTextField(
                            value = quantity,
                            onValueChange = { value ->
                                if (value.all { it.isDigit() } && value.length <= 3) quantity =
                                    value
                            },
                            label = { Text(stringResource(Res.string.inventory_item_quantity)) },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            enabled = !isLoading,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                        )
                    }
                }

                if (errorMessage != null) {
                    Text(
                        text = errorMessage,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }

                if (isLoading) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    val qty = if (item.category == ItemCategory.CONSUMABLE) {
                        quantity.toIntOrNull()?.coerceIn(1, 999) ?: 1
                    } else {
                        1
                    }
                    onConfirm(selectedCharacterIds.toList(), qty)
                },
                enabled = !isLoading && selectedCharacterIds.isNotEmpty()
            ) {
                Text(stringResource(Res.string.gm_inventory_send_button))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss, enabled = !isLoading) {
                Text(stringResource(Res.string.cancel))
            }
        }
    )
}

